/**
 * @description 同步盘「目录级排除」匹配工具
 *
 * 两类匹配语义（任意层级）：
 *  A. 条目不含 `/`（如 `node_modules`）：按相对路径切段，任一段 === 条目即命中（任意层级目录名）。
 *  B. 条目含 `/`（如 `a/b`、`packages/web/dist`）：条目的段序列作为「连续子序列」
 *     出现在相对路径的段序列中（对齐目录边界）即命中（任意层级路径前缀）。
 *
 * 该工具同时服务于：fastListFolder 递归剪枝、LocalWatcher 全量/事件过滤、
 * RemoteWatcher journal 过滤、chokidar 函数式 ignored。务必保证三处行为一致。
 */

/**
 * @description 单个排除目录条目的最大长度
 */
export const IGNORE_ENTRY_MAX_LENGTH = 255;

/**
 * @description 文件/目录名中的非法字符（Windows 受限字符集，跨平台统一禁用）
 */
const INVALID_CHAR_REG = /[\\:*?"<>|]/;

export type IgnoreEntryInvalidReason = 'empty' | 'tooLong' | 'invalidChar' | 'absolute' | 'dotdot';

/**
 * @description 校验单个排除目录条目是否合法（用于输入时即时校验，给用户反馈）
 * @param raw 用户输入的单项
 * @return null 表示合法；否则返回不合法原因
 */
export function validateIgnoreEntry(raw: string): IgnoreEntryInvalidReason | null {
  if (typeof raw !== 'string') return 'empty';
  const trimmed = raw.trim();
  if (/^([/\\]|[a-zA-Z]:[\\/])/.test(trimmed)) return 'absolute';
  const entry = trimmed.replace(/\/+$/, '');
  if (!entry || entry === '.') return 'empty';
  if (entry.length > IGNORE_ENTRY_MAX_LENGTH) return 'tooLong';
  if (INVALID_CHAR_REG.test(entry)) return 'invalidChar';

  const segments = entry.split('/').filter(seg => seg !== '' && seg !== '.');
  if (segments.length === 0) return 'empty';
  if (segments.some(seg => /^[a-zA-Z]:$/.test(seg))) return 'absolute';
  if (segments.some(seg => seg === '..')) return 'dotdot';
  return null;
}

/**
 * @description 把路径统一为 `/` 分隔，并切成非空段数组
 */
function toSegments(p: string): string[] {
  return p
    .replace(/\\/g, '/')
    .split('/')
    .filter(seg => seg !== '' && seg !== '.');
}

/**
 * @description 归一化用户输入的排除清单
 * - 去首尾空白、统一 `/`、去末尾 `/`
 * - 过滤空项、`.`、含 `..` 的项、绝对路径（以 `/` 开头或带盘符 `X:`）
 * - 去重，保持原有顺序
 * @param list 用户输入清单
 * @return 归一化后的清单
 */
export function normalizeIgnoreList(list: string[] | undefined | null): string[] {
  if (!Array.isArray(list)) return [];
  const result: string[] = [];
  const seen = new Set<string>();

  list.forEach(raw => {
    if (typeof raw !== 'string') return;
    // 统一分隔符并去首尾空白
    let entry = raw.replace(/\\/g, '/').trim();
    // 仅允许相对路径；不能把绝对路径静默降级成任意层级目录规则
    if (entry.startsWith('/') || /^[a-zA-Z]:\//.test(entry)) return;
    entry = entry.replace(/\/+$/, '');
    if (!entry) return;

    const segments = toSegments(entry);
    if (segments.length === 0) return;
    // 过滤非法：含 `..`、含盘符（绝对路径）
    if (segments.some(seg => seg === '..' || /^[a-zA-Z]:$/.test(seg))) return;

    const normalized = segments.join('/');
    if (seen.has(normalized)) return;
    seen.add(normalized);
    result.push(normalized);
  });

  return result;
}

/**
 * @description 判断 sub 段序列是否作为「连续子序列」出现在 segs 中
 * @return 命中的末段下标（segs 中 sub 最后一段的位置）；未命中返回 -1
 */
function indexOfContiguous(segs: string[], sub: string[]): number {
  if (sub.length === 0 || sub.length > segs.length) return -1;
  const limit = segs.length - sub.length;
  for (let i = 0; i <= limit; i++) {
    let matched = true;
    for (let j = 0; j < sub.length; j++) {
      if (segs[i + j] !== sub[j]) {
        matched = false;
        break;
      }
    }
    if (matched) return i + sub.length - 1;
  }
  return -1;
}

/**
 * @description 根据排除清单生成匹配函数
 *
 * 语义：排除的是「目录」。命中分两类：
 *  - 祖先命中：被排条目命中的片段不是路径末段（即当前节点位于被忽略目录之下）→ 一律排除。
 *  - 自身命中：被排条目恰好命中路径末段（当前节点自身名为 node_modules / a/b）→
 *    仅当该节点是目录时才排除；普通文件不排除（避免误伤名为 node_modules 的文件）。
 *
 * @param ignoreList 排除清单（内部会做归一化）
 * @return (relPath, isDir?) => boolean；命中被排除规则返回 true；空清单恒返回 false。
 *   isDir 省略/undefined 表示「未知」，此时自身命中也按排除处理（保持激进，
 *   供 chokidar 等拿不到类型的场景从源头剪枝，正确性由全量扫描/事件层用准确 isDir 兜底）。
 */
export function createIgnoreMatcher(
  ignoreList: string[] | undefined | null,
): (relPath: string, isDir?: boolean) => boolean {
  const normalized = normalizeIgnoreList(ignoreList);
  if (normalized.length === 0) {
    return () => false;
  }

  // 条目分两类：纯目录名（任意层级目录名匹配） / 含 `/`（任意层级路径前缀）
  const nameSet = new Set<string>();
  const pathEntries: string[][] = [];
  normalized.forEach(entry => {
    if (entry.includes('/')) {
      pathEntries.push(entry.split('/'));
    } else {
      nameSet.add(entry);
    }
  });

  return (relPath: string, isDir?: boolean): boolean => {
    if (!relPath) return false;
    const segs = toSegments(relPath);
    if (segs.length === 0) return false;

    const lastIdx = segs.length - 1;
    // 自身命中（命中末段）是否允许排除：未知类型按允许（激进），已知则仅目录可排
    const selfAllowed = isDir !== false;

    // 规则 A：任意层级目录名
    if (nameSet.size > 0) {
      for (let i = 0; i < segs.length; i++) {
        if (nameSet.has(segs[i])) {
          if (i < lastIdx) return true; // 祖先命中：当前节点在被忽略目录之下
          if (selfAllowed) return true; // 自身命中：仅目录（或未知）排除
        }
      }
    }
    // 规则 B：任意层级连续路径前缀
    for (let k = 0; k < pathEntries.length; k++) {
      const endIdx = indexOfContiguous(segs, pathEntries[k]);
      if (endIdx === -1) continue;
      if (endIdx < lastIdx) return true; // 祖先命中
      if (selfAllowed) return true; // 自身命中：仅目录（或未知）排除
    }
    return false;
  };
}
